import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../config/api';
import { ToastContext } from '../App';

interface Settings {
  app_name?: { value: string };
  email_signature?: { value: string };
  contact_email?: { value: string };
  contact_phone?: { value: string };
  booking_duration_minutes?: { value: string };
  min_booking_hours_ahead?: { value: string };
  weather_location?: { value: string };
  weather_api_key?: { value: string };
  bookings_blocked?: { value: string };
  [key: string]: any;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    app_name: '',
    email_signature: '',
    contact_email: '',
    contact_phone: '',
    booking_duration_minutes: '',
    min_booking_hours_ahead: '',
    weather_location: '',
    weather_api_key: '',
    bookings_blocked: '0'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const { showToast } = React.useContext(ToastContext);
  const [showWeatherApiKey, setShowWeatherApiKey] = useState(false);

  // Debug: mostrar informações do utilizador
  console.log('SettingsPage - User:', user);
  console.log('SettingsPage - User role:', user?.role);

  const togglePasswordVisibility = () => {
    setShowWeatherApiKey(!showWeatherApiKey);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await apiCall('/settings');
        if (data.settings) {
          // Mapear as configurações para os campos do formulário
          const fieldMappings = {
            'app_name': 'app_name',
            'email_signature': 'email_signature',
            'contact_email': 'contact_email',
            'contact_phone': 'contact_phone',
            'booking_duration_minutes': 'booking_duration_minutes',
            'min_booking_hours_ahead': 'min_booking_hours_ahead',
            'weather_location': 'weather_location',
            'weather_api_key': 'weather_api_key',
            'bookings_blocked': 'bookings_blocked'
          };

          const newForm: any = {};
          for (const [settingKey, formKey] of Object.entries(fieldMappings)) {
            newForm[formKey] = data.settings[settingKey]?.value || '';
          }
          setForm(newForm);
        } else {
          setFormError(data.error || 'Erro ao carregar configurações');
          showToast(data.error || 'Erro ao carregar configurações', 'error');
        }
      } catch {
        setFormError('Erro ao comunicar com o servidor');
        showToast('Erro ao comunicar com o servidor', 'error');
      } finally {
        // setLoading(false); // Originalmente estava aqui, mas settings, loading, error foram removidos
      }
    };
    
    if (user?.role === 'trainer') {
      fetchSettings();
    }
  }, [user]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target.checked ? '1' : '0') : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const data = await apiCall('/settings', {
        method: 'PUT',
        body: JSON.stringify(form)
      });
      if (data.message) {
        showToast('Configurações guardadas com sucesso', 'success');
      } else {
        setFormError(data.error || 'Erro ao guardar configurações');
        showToast(data.error || 'Erro ao guardar configurações', 'error');
      }
    } catch (error: any) {
      setFormError('Erro ao comunicar com o servidor');
      showToast('Erro ao comunicar com o servidor', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  if (user?.role !== 'trainer') {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Acesso restrito a treinadores.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header bg-dark text-white">
              <h5><i className="fas fa-cog me-2"></i>Configurações Gerais</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSave}>
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">
                      <i className="fas fa-info-circle me-2"></i>Informações Gerais
                    </h6>
                    
                    <div className="mb-3">
                      <label htmlFor="app_name" className="form-label">Nome da Aplicação</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        id="app_name"
                        name="app_name"
                        value={form.app_name}
                        onChange={handleFormChange}
                        required 
                      />
                      <div className="form-text">Nome que aparece no título da aplicação</div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="email_signature" className="form-label">Assinatura dos Emails</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        id="email_signature"
                        name="email_signature"
                        value={form.email_signature}
                        onChange={handleFormChange}
                        required 
                      />
                      <div className="form-text">Assinatura que aparece no final dos emails</div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="contact_email" className="form-label">Email de Contacto</label>
                      <input 
                        type="email" 
                        className="form-control" 
                        id="contact_email"
                        name="contact_email"
                        value={form.contact_email}
                        onChange={handleFormChange}
                      />
                      <div className="form-text">Email para contacto geral</div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="contact_phone" className="form-label">Telefone de Contacto</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        id="contact_phone"
                        name="contact_phone"
                        value={form.contact_phone}
                        onChange={handleFormChange}
                      />
                      <div className="form-text">Telefone para contacto</div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <h6 className="text-success mb-3">
                      <i className="fas fa-calendar-alt me-2"></i>Configurações de Marcações
                    </h6>
                    
                    <div className="mb-3">
                      <label htmlFor="booking_duration_minutes" className="form-label">Duração das Aulas (minutos)</label>
                      <input 
                        type="number" 
                        className="form-control" 
                        id="booking_duration_minutes"
                        name="booking_duration_minutes"
                        value={form.booking_duration_minutes}
                        onChange={handleFormChange}
                        min="15" 
                        max="120" 
                        step="15" 
                        required 
                      />
                      <div className="form-text">Duração padrão das aulas em minutos</div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="min_booking_hours_ahead" className="form-label">Horas Mínimas de Antecedência</label>
                      <input 
                        type="number" 
                        className="form-control" 
                        id="min_booking_hours_ahead"
                        name="min_booking_hours_ahead"
                        value={form.min_booking_hours_ahead}
                        onChange={handleFormChange}
                        min="1" 
                        max="168" 
                        required 
                      />
                      <div className="form-text">Horas mínimas de antecedência para marcar aulas (apenas alunos)</div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="form-check">
                        <input 
                          type="checkbox" 
                          className="form-check-input" 
                          id="bookings_blocked"
                          name="bookings_blocked"
                          checked={form.bookings_blocked === '1'}
                          onChange={handleFormChange}
                        />
                        <label className="form-check-label" htmlFor="bookings_blocked">
                          Marcações Bloqueadas
                        </label>
                      </div>
                      <div className="form-text">Quando ativado, os alunos não podem fazer marcações (mostra mensagem de erro)</div>
                    </div>
                  </div>
                </div>
                
                <div className="row mt-4">
                  <div className="col-md-12">
                    <h6 className="text-warning mb-3">
                      <i className="fas fa-cloud-sun me-2"></i>Configurações de Previsão do Tempo
                    </h6>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="weather_location" className="form-label">Localização para Previsão</label>
                          <input 
                            type="text" 
                            className="form-control" 
                            id="weather_location"
                            name="weather_location"
                            value={form.weather_location}
                            onChange={handleFormChange}
                            placeholder="Seixal, Portugal" 
                            required 
                          />
                          <div className="form-text">Localização para obter a previsão do tempo (cidade, país)</div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="weather_api_key" className="form-label">Chave da API OpenWeatherMap</label>
                          <div className="input-group">
                            <input 
                              type={showWeatherApiKey ? "text" : "password"} 
                              className="form-control" 
                              id="weather_api_key"
                              name="weather_api_key"
                              value={form.weather_api_key}
                              onChange={handleFormChange}
                              placeholder="Sua chave da API" 
                            />
                            <button 
                              className="btn btn-outline-secondary" 
                              type="button" 
                              onClick={togglePasswordVisibility}
                            >
                              <i className={`fas fa-${showWeatherApiKey ? 'eye-slash' : 'eye'}`}></i>
                            </button>
                          </div>
                          <div className="form-text">
                            <a href="https://openweathermap.org/api" target="_blank" rel="noreferrer">
                              <i className="fas fa-external-link-alt me-1"></i>Obter chave gratuita
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="alert alert-info">
                      <h6><i className="fas fa-info-circle me-2"></i>Como configurar a previsão do tempo:</h6>
                      <ol className="mb-0">
                        <li>Aceda a <a href="https://openweathermap.org/api" target="_blank" rel="noreferrer">openweathermap.org/api</a></li>
                        <li>Crie uma conta gratuita</li>
                        <li>Obtenha uma chave de API gratuita</li>
                        <li>Cole a chave no campo acima</li>
                        <li>Confirme a localização do treino</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex flex-column flex-md-row justify-content-center">
                  <button 
                    type="submit" 
                    className="btn btn-primary w-100 mb-2 mb-md-0"
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-1"></i>A guardar...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-1"></i>Guardar Configurações
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary ms-md-2 w-100"
                    onClick={() => window.location.reload()}
                  >
                    <i className="fas fa-sync-alt me-1"></i>Recarregar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Toast global */}
      {/* The ToastContext handles its own rendering */}
    </div>
  );
};

export default SettingsPage; 